import { useEffect, useMemo, useState } from 'react';
import { IFeesService, ISalesforceService } from '../services';
import { Application, CardFeeDetailsV2DTO, Catalogue, PaymentMethodAPI } from '../types';
import { getCatalogue } from '../helpers/get-catalogue';
import { ProductDetailsAPI } from '../types/salesforce/api/products/product-details-api';
import { filterDuplicates } from 'src/helpers/functional-helpers';
import { Environment } from 'src/Environment';
import { TerminalFee } from 'src/features/common/services/fees-service';

export const MAX_DEVICES_PER_ORDER = 70;
export enum ProductCategory {
  E_COMMERCE_GATEWAY = 'E commerce Gateway',
  VODAPAY_MAX = 'VodaPay Max',
}

export function useCatalogue(
  salesforceService: ISalesforceService,
  feesService: IFeesService,
  application: Application,
) {
  const [products, setProducts] = useState<ProductDetailsAPI[] | undefined>(undefined);
  const [cardFees, setCardFees] = useState<CardFeeDetailsV2DTO | undefined>(undefined);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodAPI[] | undefined>(undefined);
  const [terminalFees, setTerminalFees] = useState<TerminalFee[] | undefined>(undefined);
  const [useFallback, setUseFallback] = useState<boolean>(false);

  const catalogue = useMemo<Catalogue>(
    () =>
      getCatalogue(
        application,
        products,
        cardFees,
        paymentMethods,
        application?.opportunity?.solutions,
        terminalFees,
      ),
    [products, application, cardFees, paymentMethods],
  );

  const applicationCardFeesEnabled = useMemo(
    () => !Environment.current.disabledFeatures.applicationCardFees,
    [],
  );

  useEffect(() => {
    const turnover = application?.businessDetails?.estimatedAmount;
    if (turnover !== undefined && !applicationCardFeesEnabled) {
      setCardFees(undefined);
      const abortControllerCardFees = new AbortController();
      feesService
        .getCardFeesV2([], turnover, undefined, abortControllerCardFees)
        .then((response) => {
          setCardFees(response);
        });
      return () => {
        abortControllerCardFees.abort();
      };
    }
  }, [application?.businessDetails?.estimatedAmount]);

  useEffect(() => {
    const abortControllerProducts = new AbortController();

    if (terminalFees || useFallback) {
      salesforceService.getProducts(abortControllerProducts).then((products) => {
        if (!abortControllerProducts.signal.aborted) {
          setProducts(products.products);
          setPaymentMethods(
            filterDuplicates<PaymentMethodAPI>(products.paymentMethods, 'paymentMethodCode'),
          );
        }
      });
    }

    return () => abortControllerProducts.abort();
  }, [terminalFees]);

  useEffect(() => {
    const abortControllerTerminalFees = new AbortController();

    feesService
      .getTerminalFees(abortControllerTerminalFees)
      .then((terminalFees) => {
        if (!abortControllerTerminalFees.signal.aborted) {
          setTerminalFees(terminalFees);
        }
      })
      .catch(() => setUseFallback(true));

    return () => abortControllerTerminalFees.abort();
  }, []);

  return [catalogue];
}
